import { useState } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { mapEmployeeList, normalizeInfinityCode } from "../utils/employeeUtils";
import { decodeTokenPayload } from "../utils/tokenUtils";

const useEmployees = ({ BASE_URL, language, viewType, profile }) => {
  const [employees, setEmployees] = useState([]);
  const [visibleEmployees, setVisibleEmployees] = useState([]);
  // Starts true (not false) so the page-level skeleton is guaranteed to be
  // visible on first paint instead of racing the mount effect that flips it.
  const [loading, setLoading] = useState(true);

  const resolveManagerContext = () => {
    const payload = decodeTokenPayload();
    const storedClientName = profile?.clientName || localStorage.getItem('clientName');
    const storedManager = normalizeInfinityCode(profile?.infinityCode || localStorage.getItem('infinityCode'));
    const payloadManager = normalizeInfinityCode(payload?.infinityCode || payload?.employeeInfinityCode);
    const payloadClient = payload?.clientName || payload?.employeeClientName || payload?.employee?.clientName;

    return {
      clientName: storedClientName || payloadClient || '',
      managerCode: storedManager || payloadManager || null,
    };
  };

  const fetchHierarchyFallback = async ({ clientName, managerCode }) => {
    if (!clientName || !managerCode) {
      console.warn('Hierarchy fallback skipped due to missing clientName or managerCode', { clientName, managerCode });
      return [];
    }

    const candidates = Array.from(new Set([
      clientName,
      clientName.replace(/\s+/g, ''),
    ])).filter(Boolean);

    for (const candidate of candidates) {
      try {
        const hierarchyUrl = `${BASE_URL}/employees/clients/${encodeURIComponent(candidate)}/hierarchy?managerInfinityCode=${encodeURIComponent(managerCode)}`;
        const resp = await apiFetch(hierarchyUrl, {
          method: 'GET',
          headers: {
            'Accept-Language': language,
            'X-View-Type': 'manager',
          },
        });

        if (!resp.ok) {
          console.warn(`Hierarchy fallback request failed for client "${candidate}" with status ${resp.status}`);
          continue;
        }

        const json = await resp.json();
        const data = json?.hierarchy || json?.employees || json?.data || json;
        const mapped = mapEmployeeList(data);

        if (mapped.length) {
          console.log(`✅ Loaded ${mapped.length} employees via hierarchy fallback for manager ${managerCode}`);
          return mapped;
        }
      } catch (err) {
        console.warn(`Hierarchy fallback error for client "${candidate}"`, err);
      }
    }

    console.warn('Hierarchy fallback did not return any employees');
    return [];
  };

  const loadEmployees = async (retryCount = 0) => {
    try {
      if (retryCount === 0) setLoading(true);
      const { clientName, managerCode } = resolveManagerContext();
      const storedViewType = viewType || localStorage.getItem('managerViewType') || 'manager';
      const normalizedViewType = (storedViewType || 'manager').toLowerCase();
      const effectiveViewType = normalizedViewType === 'user' ? 'manager' : normalizedViewType;

      const usp = new URLSearchParams();
      if (clientName) usp.append('clientName', clientName);
      usp.append('viewType', effectiveViewType);
      if (effectiveViewType === 'manager' && managerCode) {
        usp.append('managerInfinityCode', managerCode);
      }

      const url = `${BASE_URL}/employees/visibility?${usp.toString()}`;
      const resp = await apiFetch(url, {
        method: 'GET',
        headers: {
          "Accept-Language": language,
          "x-view-type": effectiveViewType,
        }
      });

      if (!resp.ok) {
        console.warn('⚠️ /employees/visibility request failed:', resp.status);
        setLoading(false);
        return { all: [], visible: [] };
      }

      const json = await resp.json();
      let mapped = mapEmployeeList(json.employees || json.data || json);
      let usedHierarchyFallback = false;

      const shouldFallback = mapped.length === 0 && effectiveViewType === 'manager' && retryCount === 0;

      if (shouldFallback) {
        console.warn('No employees returned from visibility endpoint, attempting hierarchy fallback');
        const fallbackMapped = await fetchHierarchyFallback({ clientName, managerCode });
        if (fallbackMapped.length) {
          mapped = fallbackMapped;
          usedHierarchyFallback = true;
        }
      }

      setEmployees(mapped);
      setVisibleEmployees(mapped);

      const sourceLabel = usedHierarchyFallback ? 'hierarchy fallback' : '/employees/visibility';
      console.log(`✅ Loaded ${mapped.length} visible employees from ${sourceLabel}`);
      return { all: mapped, visible: mapped };
    } catch (error) {
      console.error("❌ Error loading visible employees:", error);
      return { all: [], visible: [] };
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeName = (employeeId, attendanceData = [], language) => {
    const employee = employees.find(
      (e) => String(e.employeeId) === String(employeeId)
    );

    let displayName = null;
    if (employee) {
      if (language === "ar") {
        displayName = employee.arabicName && employee.arabicName.trim()
          ? employee.arabicName.trim()
          : employee.name && employee.name.trim()
            ? employee.name.trim()
            : null;
      } else {
        displayName = employee.name && employee.name.trim()
          ? employee.name.trim()
          : employee.arabicName && employee.arabicName.trim()
            ? employee.arabicName.trim()
            : null;
      }
    }

    if (!displayName || !displayName.trim()) {
      const attendanceRecord = attendanceData.find(
        (r) => String(r.employeeId) === String(employeeId)
      );
      if (attendanceRecord) {
        displayName =
          (language === "ar"
            ? attendanceRecord.employee?.arabicName || attendanceRecord.employee?.name_ar
            : attendanceRecord.employee?.name || attendanceRecord.employee?.sr || attendanceRecord.employee?.fullName || attendanceRecord.employee?.englishName)
          || attendanceRecord.employeeName
          || null;
      }
    }

    if (!displayName || !displayName.trim()) {
      console.warn(`⚠️ Employee name not found for ID: ${employeeId}`);
      return String(employeeId);
    }

    return displayName.trim().split(/\s+/).slice(0, 3).join(" ");
  };

  return { employees, visibleEmployees, loading, loadEmployees, getEmployeeName, resolveManagerContext };
};

export default useEmployees;